import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../graphpad.model';
import type {
  CreatedElement,
  ElementContext,
  ElementCreator,
  EvaluableElement,
  FunctionElement,
} from '../element.model';
import { isEvaluableElement, isFunctionElement } from '../element.model';

/**
 * Interface for feature creators.
 * Features are computed elements that depend on a parent function.
 */
export interface FeatureCreator extends ElementCreator<FeatureElementDef> {
  /** The feature type this creator handles */
  readonly featureType: FeatureType;
}

/**
 * Base class for feature creators.
 * Provides common utilities for resolving parent functions.
 */
export abstract class BaseFeatureCreator implements FeatureCreator {
  abstract readonly featureType: FeatureType;

  get types(): string[] {
    return [this.featureType];
  }

  /**
   * Get a single parent function element from the params.
   * For features that depend on one function (tangent, secant, handles, etc.)
   * @throws Error if parent element doesn't exist or isn't a function
   */
  protected getParentFunction(parentId: string, context: ElementContext): FunctionElement {
    const parentElement = context.elements.get(parentId);
    if (!parentElement || !isFunctionElement(parentElement)) {
      throw new Error(
        `${this.featureType} feature requires a function element. Got: ${parentElement?.type ?? 'undefined'}`,
      );
    }
    return parentElement;
  }

  /**
   * Get a single parent element that can be evaluated as f(x).
   * For features that only need to evaluate the function (roots, intersection).
   * This includes both FunctionElements and derivative features.
   * @throws Error if parent element doesn't exist or can't be evaluated
   */
  protected getEvaluableParent(parentId: string, context: ElementContext): EvaluableElement {
    const parentElement = context.elements.get(parentId);
    if (!parentElement || !isEvaluableElement(parentElement)) {
      throw new Error(
        `${this.featureType} feature requires an evaluable element. Got: ${parentElement?.type ?? 'undefined'}`,
      );
    }
    return parentElement;
  }

  /**
   * Get two parent function elements from the params.
   * For features that depend on two functions (intersection)
   * @throws Error if parent elements don't exist or aren't functions
   */
  protected getParentFunctions(
    parentRefs: [string, string],
    context: ElementContext,
  ): [FunctionElement, FunctionElement] {
    const [id1, id2] = parentRefs;
    const el1 = context.elements.get(id1);
    const el2 = context.elements.get(id2);

    if (!el1 || !isFunctionElement(el1)) {
      throw new Error(
        `${this.featureType} feature requires function elements. First element: ${el1?.type ?? 'undefined'}`,
      );
    }
    if (!el2 || !isFunctionElement(el2)) {
      throw new Error(
        `${this.featureType} feature requires function elements. Second element: ${el2?.type ?? 'undefined'}`,
      );
    }

    return [el1, el2];
  }

  /**
   * Get two parent elements that can be evaluated as f(x).
   * For features that only need to evaluate the functions (intersection).
   * This includes both FunctionElements and derivative features.
   * @throws Error if parent elements don't exist or can't be evaluated
   */
  protected getEvaluableParents(
    parentRefs: [string, string],
    context: ElementContext,
  ): [EvaluableElement, EvaluableElement] {
    const [id1, id2] = parentRefs;
    const el1 = context.elements.get(id1);
    const el2 = context.elements.get(id2);

    if (!el1 || !isEvaluableElement(el1)) {
      throw new Error(
        `${this.featureType} feature requires evaluable elements. First element: ${el1?.type ?? 'undefined'}`,
      );
    }
    if (!el2 || !isEvaluableElement(el2)) {
      throw new Error(
        `${this.featureType} feature requires evaluable elements. Second element: ${el2?.type ?? 'undefined'}`,
      );
    }

    return [el1, el2];
  }

  abstract create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement;
}
